import moment from 'moment';

/**
 * Helper utilities for BoardGovernanceNew component.
 * Extracted from index.tsx to keep the component file focused on rendering logic.
 */

/** Convert a date string (standard or "YYYYQX" format) to a comparable timestamp */
export const getDateTimestamp = (date: string): number => {
  // Check if date is in "YYYYQX" format (e.g., "2025Q1")
  const quarterMatch = date?.match(/^(\d{4})Q([1-4])$/);
  if (quarterMatch) {
    const year = quarterMatch[1];
    const quarter = parseInt(quarterMatch[2], 10);
    // Return first day of the quarter as timestamp
    const month = (quarter - 1) * 3 + 1;
    return moment(`${year}-${String(month).padStart(2, '0')}-01`).valueOf();
  }

  // Otherwise, parse as standard date format
  return moment(date).valueOf();
};

/** Get quarter string (e.g. "Q1") from a date string */
export const getQuarterFromDate = (date: string): string => {
  // Check if date is in "YYYYQX" format (e.g., "2025Q1")
  const quarterMatch = date?.match(/^\d{4}Q([1-4])$/);
  if (quarterMatch) {
    return `Q${quarterMatch[1]}`;
  }

  // Otherwise, parse as standard date format
  const month = moment(date).month() + 1; // moment months are 0-indexed
  if (month >= 1 && month <= 3) return 'Q1';
  if (month >= 4 && month <= 6) return 'Q2';
  if (month >= 7 && month <= 9) return 'Q3';
  return 'Q4';
};

/** Determine whether a quarter is completed, current, or future */
export const getQuarterType = (quarterName: string, year?: number | string): 'completed' | 'current' | 'future' => {
  // If year is not provided or empty, return 'future' as default
  if (!year) return 'future';

  const targetYear = typeof year === 'number' ? year : parseInt(year, 10);

  // If year parsing failed, return 'future'
  if (isNaN(targetYear)) return 'future';

  const quarterMap: Record<string, { start: string; end: string }> = {
    'Q1': { start: `${targetYear}-01-01`, end: `${targetYear}-03-31` },
    'Q2': { start: `${targetYear}-04-01`, end: `${targetYear}-06-30` },
    'Q3': { start: `${targetYear}-07-01`, end: `${targetYear}-09-30` },
    'Q4': { start: `${targetYear}-10-01`, end: `${targetYear}-12-31` }
  };

  const dateRange = quarterMap[quarterName];
  if (!dateRange) return 'future';

  const now = moment();
  const startDate = moment(dateRange.start);
  const endDate = moment(dateRange.end);

  if (now.isAfter(endDate)) {
    return 'completed';
  } else if (now.isBetween(startDate, endDate, 'day', '[]')) {
    // Special handling: If current quarter is Q4, treat it as completed
    if (quarterName === 'Q4') {
      return 'completed';
    }
    return 'current';
  } else {
    return 'future';
  }
};

/** Determine whether a year is completed, current, or future */
export const getYearType = (year: number | string): 'completed' | 'current' | 'future' => {
  const targetYear = typeof year === 'number' ? year : parseInt(year, 10);

  // If year parsing failed, return 'future'
  if (isNaN(targetYear)) return 'future';

  const now = moment();
  const currentYear = now.year();

  if (targetYear < currentYear) {
    return 'completed';
  } else if (targetYear === currentYear) {
    return 'current';
  } else {
    return 'future';
  }
};

/** Get the previous quarter's year and quarter string (e.g. { targetYear: 2025, targetQuarterString: "Q4" }) */
export const getPreviousQuarter = (): { targetYear: number; targetQuarterString: string } => {
  const now = moment();
  let targetYear = now.year();
  const currentMonth = now.month() + 1;
  let currentQuarter: number;
  if (currentMonth <= 3) currentQuarter = 1;
  else if (currentMonth <= 6) currentQuarter = 2;
  else if (currentMonth <= 9) currentQuarter = 3;
  else currentQuarter = 4;

  let prevQuarter = currentQuarter - 1;
  if (prevQuarter === 0) { prevQuarter = 4; targetYear -= 1; }

  return { targetYear, targetQuarterString: `Q${prevQuarter}` };
};
